import { useMemo, useState } from "react";
import dayjs from "dayjs";
import TradesTable from "@/components/TradesTable";
import { pnlFmt } from "@/lib/format";

const PERIODS = [
  { key: "all", label: "ALL" },
  { key: "today", label: "TODAY" },
  { key: "week", label: "LAST WEEK" },
  { key: "month", label: "LAST MONTH" },
  { key: "custom", label: "CUSTOM" },
];

const dateInputCls =
  "rounded-md border border-[#2A2E39] bg-[#1E222D] px-2 py-1.5 font-mono text-xs text-[#EAECEF] [color-scheme:dark] focus:border-[#F0B90B]/50 focus:outline-none";

const isValidDateStr = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (!m) return false;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  return y >= 1970 && y <= 2100 && mo >= 1 && mo <= 12 && d >= 1 && d <= 31 && dayjs(v).isValid();
};

function DateField({ value, onChange, testid }) {
  const handle = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 8);
    let v = digits;
    if (digits.length > 4) v = `${digits.slice(0, 4)}-${digits.slice(4)}`;
    if (digits.length > 6) v = `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6)}`;
    onChange(v);
  };
  const invalid = value.length === 10 && !isValidDateStr(value);
  return (
    <input
      type="text"
      inputMode="numeric"
      data-testid={testid}
      value={value}
      onChange={handle}
      placeholder="YYYY-MM-DD"
      maxLength={10}
      className={`${dateInputCls} w-[130px] ${invalid ? "border-[#F6465D]" : ""}`}
    />
  );
}

export default function TradeHistorySection({ trades, openCount }) {
  const [period, setPeriod] = useState("all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  const filtered = useMemo(() => {
    if (period === "all") return trades;
    if (period === "custom" && (!isValidDateStr(start) || !isValidDateStr(end))) return trades;
    let from;
    let to = dayjs().endOf("day");
    if (period === "today") from = dayjs().startOf("day");
    else if (period === "week") from = dayjs().subtract(7, "day").startOf("day");
    else if (period === "month") from = dayjs().subtract(1, "month").startOf("day");
    else {
      from = dayjs(start).startOf("day");
      to = dayjs(end).endOf("day");
    }
    return trades.filter((t) => {
      const d = dayjs(t.open_time);
      return !d.isBefore(from) && !d.isAfter(to);
    });
  }, [trades, period, start, end]);

  const closed = filtered.filter((t) => t.status === "closed");
  const netPnl = closed.reduce((s, t) => s + (Number(t.profit) || 0), 0);
  const wins = closed.filter((t) => t.profit > 0).length;
  const losses = closed.filter((t) => t.profit < 0).length;
  const winRate = closed.length ? Math.round((wins / closed.length) * 100) : 0;
  const best = closed.length ? Math.max(...closed.map((t) => t.profit)) : 0;
  const worst = closed.length ? Math.min(...closed.map((t) => t.profit)) : 0;

  const cells = [
    { label: "Net P&L", value: pnlFmt(netPnl), color: netPnl > 0 ? "#0ECB81" : netPnl < 0 ? "#F6465D" : "#EAECEF", testid: "period-net-pnl" },
    { label: "Closed Trades", value: closed.length, color: "#EAECEF", testid: "period-trade-count" },
    { label: "Wins", value: wins, color: "#0ECB81", testid: "period-wins" },
    { label: "Losses", value: losses, color: "#F6465D", testid: "period-losses" },
    { label: "Win Rate", value: `${winRate}%`, color: "#EAECEF", testid: "period-win-rate" },
    { label: "Best / Worst", value: `${pnlFmt(best)} / ${pnlFmt(worst)}`, color: "#848E9C", testid: "period-best-worst" },
  ];

  return (
    <div className="space-y-3 fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-lg font-semibold text-[#EAECEF]">Trade History</h2>
        <span className="font-mono text-xs text-[#5E6673]">{openCount} open positions</span>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="period-filter-bar">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            data-testid={`period-filter-${p.key}`}
            onClick={() => setPeriod(p.key)}
            className={`rounded-md px-3 py-1.5 text-[11px] font-bold tracking-wide transition-colors ${
              period === p.key
                ? "bg-[#F0B90B] text-black"
                : "border border-[#2A2E39] bg-[#1E222D] text-[#848E9C] hover:bg-[#2A2E39]"
            }`}
          >
            {p.label}
          </button>
        ))}
        {period === "custom" && (
          <div className="flex items-center gap-2" data-testid="custom-date-range">
            <DateField testid="custom-start-date" value={start} onChange={setStart} />
            <span className="text-xs text-[#5E6673]">to</span>
            <DateField testid="custom-end-date" value={end} onChange={setEnd} />
          </div>
        )}
      </div>

      <div
        data-testid="period-pnl-summary"
        className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-[#2A2E39] bg-[#2A2E39] sm:grid-cols-3 xl:grid-cols-6"
      >
        {cells.map((c) => (
          <div key={c.label} data-testid={c.testid} className="bg-[#131722] px-3 py-2.5">
            <div className="text-[10px] font-medium uppercase tracking-wider text-[#5E6673]">{c.label}</div>
            <div className="mt-0.5 truncate font-mono text-sm font-semibold" style={{ color: c.color }}>
              {c.value}
            </div>
          </div>
        ))}
      </div>

      <TradesTable trades={filtered} variant="client" testid="mt5-client-trade-history-table" />
    </div>
  );
}
